import type { installation } from './entites-installation';

export type TypeDefautMateriel = 'production' | 'communication';
export type EtatMateriel = {
  classe: TypeDefautMateriel | 'normal' | 'inconnu';
  libelle: string;
  icone: string;
};
export type DefautsMateriel = {
  idMateriel: number;
  idStructure?: never;
  libelle: string | null;
  type: string | null;
  defauts: { type: TypeDefautMateriel; tickets: number[] }[];
};

/** Défauts rattachés à une structure, distincts des défauts matériels. */
export type DefautsStructure = {
  idStructure: number;
  idMateriel?: never;
  libelle: string | null;
  type: 'batiment';
  defauts: DefautsMateriel['defauts'];
};

export type DefautsInstallation = {
  structures: DefautsStructure[];
  materiels: DefautsMateriel[];
};

function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}

function estIdentifiant(valeur: unknown): valeur is number {
  return typeof valeur === 'number' && Number.isInteger(valeur) && valeur > 0;
}

function estListeDefauts(valeur: unknown): valeur is DefautsMateriel['defauts'] {
  return (
    Array.isArray(valeur) &&
    valeur.every(
      (defaut) =>
        estObjet(defaut) &&
        (defaut['type'] === 'production' || defaut['type'] === 'communication') &&
        Array.isArray(defaut['tickets']) &&
        defaut['tickets'].every(estIdentifiant),
    )
  );
}

function estElementDefauts(valeur: unknown): valeur is Record<string, unknown> {
  return (
    estObjet(valeur) &&
    (valeur['libelle'] === null || typeof valeur['libelle'] === 'string') &&
    (valeur['type'] === null || typeof valeur['type'] === 'string') &&
    estListeDefauts(valeur['defauts'])
  );
}

/** Valide la frontière API sans assimiler une réponse invalide à une absence de défauts. */
export function extraireDefautsMateriels(valeur: unknown): DefautsMateriel[] {
  if (
    !estObjet(valeur) ||
    !Array.isArray(valeur['materiels']) ||
    !Array.isArray(valeur['structures'])
  ) {
    throw new Error('Réponse des défauts matériels invalide.');
  }
  const materiels = valeur['materiels'];
  const structures = valeur['structures'];
  const materielsValides = materiels.every(
    (m): m is DefautsMateriel =>
      estElementDefauts(m) && estIdentifiant(m['idMateriel']) && m['idStructure'] === undefined,
  );
  const structuresValides = structures.every(
    (s): s is DefautsStructure =>
      estElementDefauts(s) &&
      estIdentifiant(s['idStructure']) &&
      s['idMateriel'] === undefined &&
      s['type'] === 'batiment',
  );
  if (!materielsValides || !structuresValides) {
    throw new Error('Réponse des défauts matériels invalide.');
  }
  return materiels;
}

/** Structure déjà utilisée par l'assistant pour GET /installation/:id. */
export type InventaireInstallation = Partial<
  Pick<
    installation,
    | 'dataloggers'
    | 'batiments'
    | 'batteries'
    | 'antenne'
    | 'compteursRattaches'
    | 'compteursEDFRattaches'
  >
>;

export type EquipementInstallation = {
  idMateriel: number;
  libelle: string;
  type: string;
  marque: string;
  categorie: CategorieEquipement;
};

export type CategorieEquipement =
  'supervision' | 'onduleurs' | 'jonction' | 'comptage' | 'capteurs' | 'infrastructure';

/** Classe les matériels signalés par les défauts mais absents de l'inventaire. */
export function categorieEquipement(type: string | null): CategorieEquipement {
  const famille = (type ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (/datalogger|antenne|supervision|communication/.test(famille)) return 'supervision';
  if (famille.includes('onduleur')) return 'onduleurs';
  if (famille.includes('jonction')) return 'jonction';
  if (/compteur|comptage/.test(famille)) return 'comptage';
  if (/capteur|sonde/.test(famille)) return 'capteurs';
  return 'infrastructure';
}

/**
 * Déduplique les matériels par identifiant, même s'ils sont rattachés à plusieurs rampants.
 * La catégorie vient de l'inventaire, car la famille du modèle peut être plus spécifique.
 */
export function extraireEquipements(source: InventaireInstallation): EquipementInstallation[] {
  const equipements = new Map<number, EquipementInstallation>();
  const ajouter = (
    materiel: {
      id: number | null;
      nom: string;
      modele?: { famille: string; marque: string };
    },
    famille: string,
  ) => {
    const id = materiel.id;
    if (id == null || !Number.isInteger(id) || id <= 0 || equipements.has(id)) return;
    equipements.set(id, {
      idMateriel: id,
      libelle: materiel.nom?.trim() || `Matériel #${id}`,
      type: materiel.modele?.famille || famille,
      marque: materiel.modele?.marque || '',
      categorie: categorieEquipement(famille),
    });
  };
  const compteurs = (
    conteneur: Pick<InventaireInstallation, 'compteursRattaches' | 'compteursEDFRattaches'>,
  ) => {
    for (const m of conteneur.compteursRattaches ?? []) ajouter(m, 'compteur');
    for (const m of conteneur.compteursEDFRattaches ?? []) ajouter(m, 'compteur');
  };
  for (const m of source.dataloggers ?? []) ajouter(m, 'datalogger');
  for (const batiment of source.batiments ?? []) {
    for (const pente of batiment.pentes ?? []) {
      for (const m of pente.capteursIrr ?? []) ajouter(m, 'capteur');
      for (const m of pente.capteursTemp ?? []) ajouter(m, 'capteur');
      for (const m of pente.onduleursRattaches ?? []) {
        ajouter(m, 'onduleur');
        for (const boite of m.bJonctionsRattaches ?? []) {
          ajouter(boite, 'boite de jonction');
        }
      }
      compteurs(pente);
    }
    compteurs(batiment);
  }
  compteurs(source);
  for (const m of source.batteries ?? []) ajouter(m, 'batterie');
  if (source.antenne) ajouter(source.antenne, 'antenne');
  return [...equipements.values()];
}

export function etatMateriel(defauts: DefautsMateriel['defauts']): EtatMateriel {
  const production = defauts.some((d) => d.type === 'production');
  const communication = defauts.some((d) => d.type === 'communication');
  return {
    // Choix provisoire du ticket 516 : production prioritaire, sans masquer le cumul.
    classe: production ? 'production' : communication ? 'communication' : 'normal',
    libelle:
      production && communication
        ? 'Défauts de production et de communication'
        : production
          ? 'Défaut de production'
          : communication
            ? 'Défaut de communication'
            : 'Aucun défaut actif signalé',
    icone: production ? 'error' : communication ? 'wifi_off' : 'check_circle',
  };
}
